// Size not preselected (CUSTOMIZATIONS.md). When a product page opens with no size chosen, the buy buttons
// (snippets/buy-buttons.liquid, block-volume-selector.liquid, product-sticky-add-to-cart.liquid) render as plain
// type="button" elements with the `js-size-prompt` class, labelled 「サイズをお選びください」. A tap on one scrolls the size
// row into view, outlines it for a moment and shows a one-line message under it. It is never a dead, greyed button.
//
// What clears the state: the first size tap. The theme's own VariantPicker then fetches the section for that size
// (?option_values=…), and the server renders the size row and the buy box for the chosen variant (selected_variant is
// set), so both come back in their normal state. The multi-buy selector is not re-rendered by the theme, so this file
// turns its button back into a real submit button on that size's variant:change, and assets/volume-selector.js sets
// its label and tier availability as it always has.
//
// Slow phones: a tap on the button while a ticked size is still loading waits for it, then adds that size.
// Colour switches: the size chosen on one colour is ticked on the next colour of the same style (same tab).
//
// Safety net: while a form's size row still carries data-size-unselected, any submit of that form (Enter key, an app's
// own button, a sticky bar) is stopped before the theme's ProductForm handler can add the first size in stock.
//
// Events: `boody_size_prompt` goes out through Shopify.analytics.publish (customer events), like the Instagram strip's
// events, so the custom pixel analytics/ga4-size-test-pixel.js can forward it to GA4. Without the pixel nothing is recorded and the page works the same.
(function () {
  if (window.__boodySizeRequired) return;
  window.__boodySizeRequired = true;

  var reduceMotion = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  function track(name, params) {
    try {
      if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
        Shopify.analytics.publish(name, params);
      }
    } catch (e) {}
  }

  // A product form's id attribute (form.id is shadowed by its hidden <input name="id">, see the volume-selector notes).
  function formIdOf(form) {
    return form && form.getAttribute ? form.getAttribute('id') : null;
  }

  function pickerFor(formId) {
    if (!formId) return null;
    return document.querySelector('variant-picker[form-id="' + CSS.escape(formId) + '"]');
  }

  function isUnselected(formId) {
    var picker = pickerFor(formId);
    return !!(picker && picker.hasAttribute('data-size-unselected'));
  }

  function formIdForButton(button) {
    return button.getAttribute('form') || formIdOf(button.form) || formIdOf(button.closest('form'));
  }

  // Recovery when a size tap did not take. The theme fetches the section for the tapped size; if that request fails
  // (for example Shopify answering "too many requests"), the theme caches the bad answer for five minutes and the page
  // keeps showing the prompt while the tapped size looks chosen. On the main product page we then reload with that
  // size in the URL (option_values, which Shopify accepts as a deep link), so the server renders it chosen.
  var RECOVER_AFTER_MS = 4000;

  function tickedSize(picker) {
    return picker && picker.querySelector('[data-option-role="size"] input[data-option-position]:checked');
  }

  function recover(picker) {
    if (!picker || !picker.isConnected || !picker.hasAttribute('data-size-unselected') || !picker.hasAttribute('update-url')) return false;
    var values = Array.prototype.slice.call(picker.querySelectorAll('input[data-option-position]:checked'))
      .sort(function (a, b) { return parseInt(a.getAttribute('data-option-position'), 10) - parseInt(b.getAttribute('data-option-position'), 10); })
      .map(function (input) { return input.value; });
    if (!values.length) return false;

    var url = new URL(window.location.href);
    url.searchParams.delete('variant');
    url.searchParams.set('option_values', values.join(','));
    track('boody_size_prompt_recover', { handle: picker.getAttribute('handle') });
    window.location.replace(url.toString());
    return true;
  }

  // A tap on the button while a size is ticked but its section is still loading (slow phones) is not a failure: wait
  // for the theme's variant:change, then add with the real button; only if nothing arrives in time, recover.
  var WAIT_FOR_SIZE_MS = 3500;
  var pendingAdd = {};

  function waitThenAdd(formId, button) {
    if (pendingAdd[formId]) return;
    if (button) button.setAttribute('aria-busy', 'true');
    pendingAdd[formId] = window.setTimeout(function () {
      delete pendingAdd[formId];
      if (button) button.removeAttribute('aria-busy');
      recover(pickerFor(formId));
    }, WAIT_FOR_SIZE_MS);
  }

  function addIfPending(formId) {
    if (!pendingAdd[formId]) return;
    window.clearTimeout(pendingAdd[formId]);
    delete pendingAdd[formId];
    // Let the re-render and volume-selector.js settle first. A sold-out size has no enabled submit button, so nothing
    // is added and the back-in-stock button shows instead.
    window.setTimeout(function () {
      if (isUnselected(formId)) return;
      var form = document.getElementById(formId);
      var id = CSS.escape(formId);
      var button = document.querySelector('buy-buttons[form="' + id + '"] .buy-buttons__add-to-cart:not([hidden]) button[type="submit"]:not([disabled]), ' +
        '.volume-selector[data-product-form-id="' + id + '"] .volume-selector__add-to-cart:not([hidden]) button[type="submit"]:not([disabled])');
      if (!form || !button) return;
      if (typeof form.requestSubmit === 'function') form.requestSubmit(button); else button.click();
    }, 60);
  }

  // Size carry-over between sister colours of one style (red-team 2026-11-04: 37% of women's XS-first page loads come
  // from a colour switch, and an XS-default page made 15% of shoppers who had chosen another size add XS). The size
  // tapped on one colour is ticked on the next colour of the same style in this tab, if it is in stock. It is the
  // shopper's own choice from this visit, never the first size in stock.
  var CHOICE_KEY = 'boody:size-choice';

  function sizeName(label) {
    return label ? label.innerText.trim().split('\n')[0].trim() : '';
  }

  function remember(picker, input) {
    var style = picker.getAttribute('data-style-id');
    if (!style || !picker.hasAttribute('update-url')) return;
    var size = sizeName(document.querySelector('label[for="' + CSS.escape(input.id) + '"]'));
    if (!size) return;
    try { window.sessionStorage.setItem(CHOICE_KEY, JSON.stringify({ style: style, size: size })); } catch (e) {}
  }

  function carryOver() {
    var choice;
    try { choice = JSON.parse(window.sessionStorage.getItem(CHOICE_KEY) || 'null'); } catch (e) { return; }
    if (!choice || !choice.style || !choice.size) return;

    document.querySelectorAll('variant-picker[update-url][data-size-unselected][data-style-id]').forEach(function (picker) {
      if (picker.getAttribute('data-style-id') !== choice.style) return;
      var fieldset = picker.querySelector('[data-option-role="size"]');
      if (!fieldset) return;
      var label = Array.prototype.find.call(fieldset.querySelectorAll('label'), function (l) { return sizeName(l) === choice.size; });
      if (!label || label.classList.contains('is-disabled')) return;
      label.click();
      track('boody_size_carried', { handle: picker.getAttribute('handle'), size: choice.size });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { window.setTimeout(carryOver, 0); });
  } else {
    window.setTimeout(carryOver, 0);
  }

  function prompt(formId, source) {
    var picker = pickerFor(formId);
    if (!picker) return;

    var fieldset = picker.querySelector('[data-option-role="size"]');
    var message = picker.querySelector('[data-size-message]');

    if (message) {
      message.hidden = false;
      // Set the text each time so screen readers announce it again on a second tap.
      message.textContent = '';
      message.textContent = message.getAttribute('data-text');
    }

    if (fieldset) {
      fieldset.classList.remove('is-size-attention');
      void fieldset.offsetWidth; // restart the outline animation on repeated taps
      fieldset.classList.add('is-size-attention');

      // html has scroll-padding-top for the sticky header, so scrollIntoView keeps the row clear of it. Works inside
      // the quick-buy modal too, since scrollIntoView scrolls the nearest scrolling container.
      fieldset.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });

      var firstAvailable = fieldset.querySelector('input[data-option-position]:not(:disabled)');
      if (firstAvailable) {
        try { firstAvailable.focus({ preventScroll: true }); } catch (e) {}
      }
    }

    track('boody_size_prompt', { form_id: formId, source: source, handle: picker.getAttribute('handle') });
  }

  // Taps on a 「サイズをお選びください」 button.
  document.addEventListener('click', function (event) {
    var button = event.target.closest && event.target.closest('.js-size-prompt');
    if (!button) return;

    var formId = formIdForButton(button);
    if (!isUnselected(formId)) return;

    event.preventDefault();
    if (tickedSize(pickerFor(formId))) { waitThenAdd(formId, button); return; }
    var source = button.closest('product-sticky-add-to-cart') ? 'sticky_bar' : (button.closest('.volume-selector') ? 'multi_buy' : 'buy_box');
    prompt(formId, source);
  });

  // Safety net for any other way the form could be submitted before a size is chosen. Capture phase on document,
  // so it runs before ProductForm's own submit listener on the form.
  document.addEventListener('submit', function (event) {
    var formId = formIdOf(event.target);
    if (!isUnselected(formId)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    if (tickedSize(pickerFor(formId))) { waitThenAdd(formId, event.submitter || null); return; }
    prompt(formId, 'submit');
  }, true);

  // The moment a size is tapped, drop the message and outline, before the section re-render arrives.
  document.addEventListener('change', function (event) {
    var input = event.target;
    if (!input.matches || !input.matches('input[data-option-position]')) return;

    var fieldset = input.closest('[data-option-role="size"]');
    if (!fieldset) return;

    fieldset.classList.remove('is-size-attention');
    var message = fieldset.querySelector('[data-size-message]');
    if (message) message.hidden = true;

    // Normally the theme replaces this size row within a moment; if it is still the unselected one after a few
    // seconds, the request behind the tap failed, so reload with the tapped size (see recover above).
    var picker = input.closest('variant-picker');
    if (picker) remember(picker, input);
    if (picker && picker.hasAttribute('data-size-unselected')) {
      window.setTimeout(function () { recover(picker); }, RECOVER_AFTER_MS);
    }
  });

  // The multi-buy selector is not part of the theme's partial re-render, so its prompt button is switched back to a
  // real add-to-cart (submit) button here; volume-selector.js sets its label, disabled state and tiers for the size.
  document.addEventListener('variant:change', function (event) {
    var formId = formIdOf(event.target);
    if (!formId) return;

    document.querySelectorAll('.volume-selector[data-product-form-id="' + CSS.escape(formId) + '"] .js-size-prompt').forEach(function (button) {
      button.type = 'submit';
      button.classList.remove('js-size-prompt');
      button.removeAttribute('aria-busy');
    });
    addIfPending(formId);
  });
})();
